<script setup lang="ts">
import {
  $brandingGlobalLineItemOptions,
  $exportBrandingItems,
  $lineItemList,
  $multipleApproveLineItem,
  $multipleUpdateLineItemAssignee,
  $multipleUpdateLineItemOnHold,
  $multipleUpdateLineItemVisibility,
  $workOrder,
  $getLineItemWfPreviousStatuses,
  $getBrandingItemArtworkLinkList
} from '@/api/brandings'
import { $taskPolling } from '@/api/helpers'
import ProjectFilesUploadDialog from '@/components/projects/file-management/ProjectFilesUploadDialog.vue'
import ProjectFilesDialog from '@/components/projects/file-management/ProjectFilesDialog.vue'
import { useBrandingStore } from '@/store/branding'
import { debounce } from 'lodash'
import { can } from '@layouts/plugins/casl'
import { useSnackbarStore } from '@/store/snackbar'
import { useUserStore } from '@/store/user'
import { flattenDeep, uniq, capitalize } from 'lodash'
import { getCampaignItemVisibilityFilter } from '@/helpers/global-items/getCampaignItemVisibilityFilter'
import type { TableSorting, TableOptions } from '@/models/general/table'
import type { WorkOrder } from '@/models/branding/workOrder'
import type { TableHeader } from '@/store/models/table'
import type { LocationQueryValue } from 'vue-router'
import { useQueryParams } from '@/helpers/composables/updateQueryParameters'
import { downloadFreshFiles } from '@/helpers/utils/downloadAllFilesUsingLinks'
import type { ArtworkDownloadUI, DownloadProgressEvent, FreshDownloadTarget } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { defaultLineItemHeaders as constDefaultLineItemHeaders, COLUMN_TYPE_BRANDING } from '@/store/helpers/default-values'
import { $getCampaign } from '@/api/campaigns'
import { useDivisionColumnsStore } from '@/store/division-columns'
import {
  onWaitingRoomAutoSyncRefresh
} from '@/helpers/events/waitingRoomAutoSync'
import { useWaitingRoomAutoSyncStore } from '@/store/waitingRoomAutoSync'

interface IProps {
  selectedProjectType?: 0 | 1 | 2 | 5, // 0 - Retail, 1 - Wayfinding, 2 - Branding, 5 - KOP
  projectTypesList?: { title: 'Signs' | 'Line Items' | 'KOP Items', value: 1 | 2 | 5 }[],
  currentProject?: any,
  isProjectOnlyView?: boolean,
  isWfLineItems?: boolean,
  isKopLineItems?: boolean,
  isRetailLineItems?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  selectedProjectType: undefined,
  projectTypesList: (() => ([])),
  currentProject: undefined,
  isProjectOnlyView: false,
  isWfLineItems: false,
  isKopLineItems: false,
  isRetailLineItems: false,
})

interface Emit {
  (e: 'update:selectedProjectType', value: 1 | 2 | 5): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()
const brandingStore = useBrandingStore()
const userStore = useUserStore()
const waitingRoomAutoSyncStore = useWaitingRoomAutoSyncStore()
const divisionColumnsStore = useDivisionColumnsStore()

const { setQuery, customUpdateQuery } = useQueryParams()

const router = useRouter()
const route = useRoute()
const isMounted = ref<boolean>(false)
let hasInitializedFilters = false
let isDisposed = false
let activeProjectsRefreshId = 0

const workOrder = ref<WorkOrder>({})
const workOrderLoading = ref<boolean>(false)

const project = ref<any>({})

const lineItems = ref<any[]>([])
const lineItemsLoading = ref<boolean>(false)
const lineItemsCount = ref<number>(0)
const selectedLineItemIds = ref<number[]>([])
const selectedLineItemCampaignMapping = ref<Record<number, number>>({})
const selectedLineItemCampaignNameMapping = ref<Record<number, string>>({})
const selectedLineItemIsOnHoldMapping = ref<Record<number, boolean>>({})
const selectedLineItemIsCanUserSelectMapping = ref<Record<number, boolean>>({})
const selectedLineItemIsCanUserApproveMapping = ref<Record<number, boolean>>({})
const selectedLineItemFilenameMapping = ref<Record<number, string>>({})
const selectedLineItemLineNoMapping = ref<Record<number, string | number>>({})
const selectedLineItemStatusNameMapping = ref<Record<number, string | null>>({})

const approveLoading = ref<boolean>(false)
const rejectLoading = ref<boolean>(false)

const showUpdateWorkOrderDialog = ref<boolean>(false)
const showTableColumnSettingsDialog = ref<boolean>(false)
const showShareItemsByEmailDialog = ref<boolean>(false)
const showProjectFilesUploadDialog = ref<boolean>(false)
const showProjectFilesDialog = ref<boolean>(false)

const openProjectFilesUploadDialog = () => {
  showProjectFilesUploadDialog.value = true
}

// Line Item Status History
const showLineItemStatusHistoryDialog = ref<boolean>(false)
const selectedHistoryLineItemId = ref<number | null>(null)

// Line Item Chat
const showGlobalChatDialog = ref(false)
const selectedChatLineItemId = ref<number | number[] | null>(null)

// ItemStatusDialog
const showItemStatusDialog = ref<boolean>(false)

// ItemAssigneeDialog - assignee(responsible/assigned_to)
const showItemAssigneeDialog = ref<boolean>(false)
const itemAssigneeDialogLoading = ref<boolean>(false)
const selectedAssigneeType = ref<'responsible' | 'assigned_to'>('responsible')  // 'responsible' or 'assigned_to'

// ItemPriorityDialog
const showItemPriorityDialog = ref<boolean>(false)

// Item Ext Ref Num Dialog
const showItemExtRefNumDialog = ref(false)

const updateBtnLoading = ref<boolean>(false)

// filters
const lineItemSearch = ref<string>('')
const searchBy = ref<string>('description')
const searchByItems = [
  { value: 'description', title: 'Description' },
  { value: 'line_no', title: 'Line No' },
  { value: 'file_name', title: 'Filename' },
  { value: 'cr_code_ext', title: 'CR Code Ext' },
  { value: 'cr_code_int', title: 'CR Code Int' },
  { value: 'ext_ref_num', title: 'Ext Ref #' },
]

const zoneOptions = ref([])
const areaOptions = ref([])
const responsibleOptions = ref([])
const assignedToOptions = ref([])
const statusOptions = ref([])
const globalOptionsLoading = ref<boolean>(false)
const dueDateStart = ref<string | null>(null)
const dueDateEnd = ref<string | null>(null)
const hiddenVisibilityFilter = ref<boolean>(false)
const isReleasedFilter = ref<true | null>(null)
const rejectedItemsOnlyFilter = ref<true | null>(null)
const isOnHoldFilter = ref<boolean | null >(null)
const isOnHoldOptions = ref([])
const supplierErpFilter = ref<string[]>([])
const programOptions = ref([])
const supplierOptions = ref([])
const projectOptions = ref([])
const category1Options = ref([])
const category2Options = ref([])
const supplierErpOptions = ref([])
const priorityOptions = ref<any[]>([])

// Local filters for non-global branding items list page
const lineItemTableOptions = ref<TableOptions>({
  itemsPerPage: 20,
  page: 1,
  sortBy: []
})
const zoneFilter = ref<any[]>([])
const areaFilter = ref<any[]>([])
const responsibleFilter = ref<any[]>([])
const assignedToFilter = ref<any[]>([])
const statusFilter = ref<any[]>([])
const priorityFilter = ref<any[]>([])

const exportCategoryOptions = ref<('excel' | 'artwork')[]>(['excel', 'artwork'])
const selectedExportCategories = ref<('excel' | 'artwork')[]>([])
const exportLoading = ref<boolean>(false)
const showBrandingExportMenu = ref<boolean>(false)

type SharedUploadItem = {
  id: number
  lineNo: string | number
  filename: string
}
const showSharedUploadLinkDialog = ref<boolean>(false)
const sharedUploadToken = 'branding-files-upload-demo-token'
const sharedUploadLink = computed(() => {
  const linkPath = router.resolve({ path: `/shared-upload/${sharedUploadToken}` }).href

  if (typeof window === 'undefined')
    return linkPath

  return `${window.location.origin}${linkPath}`
})
const selectedSharedUploadItems = computed<SharedUploadItem[]>(() => {
  return selectedLineItemIds.value.map((id, index) => ({
    id,
    lineNo: selectedLineItemLineNoMapping.value[id] || index + 1,
    filename: selectedLineItemFilenameMapping.value[id] || `MIA-BP-PDP-${String(index + 1).padStart(2, '0')}.pdf`
  }))
})

const selectedUploadExpectedItems = computed(() => {
  return selectedLineItemIds.value
    .map(id => ({
      id,
      name: selectedLineItemFilenameMapping.value[id] || '',
      statusName: selectedLineItemStatusNameMapping.value[id] || null,
    }))
})

const showLineItemReviewDialog = ref<boolean>(false)

const selectedLineItemForReviewDialog = ref<{
  campaign_id: string | number | null,
  work_order_id: string | number | null,
  id: string | number | null
}>({
  campaign_id: null,
  work_order_id: null,
  id: null
})

// const showLineItemMultipleApproveRejectDialog = ref<boolean>(false)
// const isApproveLineItemMultipleApproveRejectDialog = ref<boolean>(false)
// const lineItemMultipleApproveRejectDialogFilters = ref<any>({})

const isLinkCreatedUser = computed(() => {
  return userStore.isLinkCreatedUser
})

const lineItemHeaders = computed(() => {
  let headers = divisionColumnsStore.restrictHeaders(
    brandingStore.lineItemHeaders,
    COLUMN_TYPE_BRANDING,
  )

  if (!can('view', 'finances'))
    headers = headers.filter((item: any) => !['price', 'total'].includes(item.key))

  if (isLinkCreatedUser.value)
    return headers.filter((item: any) => !['action', 'price', 'total'].includes(item.key))

  return headers
})

const defaultLineItemHeaders = computed(() => {
  let headers = divisionColumnsStore.restrictHeaders(
    constDefaultLineItemHeaders,
    COLUMN_TYPE_BRANDING,
  )

  if (!can('view', 'finances'))
    headers = headers.filter((item: any) => !['price', 'total'].includes(item.key))

  if (isLinkCreatedUser.value)
    return headers.filter((item: any) => !['action', 'price', 'total'].includes(item.key))

  return headers
})

const projectId = computed(() => {
  return route.params.id
})

const workOrderId = computed(() => {
  return route.params.workOrderId
})

const isGlobalSearch = computed(() => {
  return !route.params.id
})

const isBrandingGlobalSearch = computed(() => {
  return isGlobalSearch.value && !props.isWfLineItems && !props.isKopLineItems && !props.isRetailLineItems
})

const isActiveProjectsFilter = computed<boolean>(() => route.query.is_active_projects !== 'false')

const searchQuery = computed(() => {
  return route.query.search
})

const searchByQuery = computed(() => {
  return route.query['search-by']
})

const computedProject = computed(() => {
  if (props.currentProject) {
    return props.currentProject
  }
  return project.value
})

const isActiveProject = computed(() => {
  // If Project View - check Project Visibility(Is Active)
  return isGlobalSearch.value ? (!isBrandingGlobalSearch.value || isActiveProjectsFilter.value) : (
    props.isProjectOnlyView ? computedProject.value?.is_visible || false : workOrder.value?.is_visible_campaign || false
  )
})

const isActionLoading = computed(() => {
  return approveLoading.value || rejectLoading.value || updateBtnLoading.value
})

const computedLineItemTableOptions = computed<TableOptions>({
  // getter
  get() {
    return isGlobalSearch.value ? brandingStore.lineItemTableOptions : lineItemTableOptions.value
  },
  // setter
  set(newValue: TableOptions) {
    if (isGlobalSearch.value) {
      // Update the store property if it's a global search
      brandingStore.lineItemTableOptions = newValue
    } else {
      // Update the local variable if it's not a global search
      lineItemTableOptions.value = newValue
    }
  }
})

const computedResponsibleFilter = computed({
  // getter
  get() {
    return isGlobalSearch.value ? brandingStore.globalFilters.responsibleFilter : responsibleFilter.value
  },
  // setter
  set(newValue: any[]) {
    if (isGlobalSearch.value) {
      // Update the store property if it's a global search
      brandingStore.globalFilters.responsibleFilter = newValue
    } else {
      // Update the local variable if it's not a global search
      responsibleFilter.value = newValue
    }
  }
})

const computedAssignedToFilter = computed({
  // getter
  get() {
    return isGlobalSearch.value ? brandingStore.globalFilters.assignedToFilter : assignedToFilter.value
  },
  // setter
  set(newValue: any[]) {
    if (isGlobalSearch.value) {
      // Update the store property if it's a global search
      brandingStore.globalFilters.assignedToFilter = newValue
    } else {
      // Update the local variable if it's not a global search
      assignedToFilter.value = newValue
    }
  }
})

const computedStatusFilter = computed({
  // getter
  get() {
    return isGlobalSearch.value ? brandingStore.globalFilters.statusFilter : statusFilter.value
  },
  // setter
  set(newValue: any[]) {
    if (isGlobalSearch.value) {
      // Update the store property if it's a global search
      brandingStore.globalFilters.statusFilter = newValue
    } else {
      // Update the local variable if it's not a global search
      statusFilter.value = newValue
    }
  }
})

const computedZoneFilter = computed({
  // getter
  get() {
    return isGlobalSearch.value ? brandingStore.globalFilters.zoneFilter : zoneFilter.value
  },
  // setter
  set(newValue: any[]) {
    if (isGlobalSearch.value) {
      // Update the store property if it's a global search
      brandingStore.globalFilters.zoneFilter = newValue
    } else {
      // Update the local variable if it's not a global search
      zoneFilter.value = newValue
    }
  }
})

const computedAreaFilter = computed({
  // getter
  get() {
    return isGlobalSearch.value ? brandingStore.globalFilters.areaFilter : areaFilter.value
  },
  // setter
  set(newValue: any[]) {
    if (isGlobalSearch.value) {
      // Update the store property if it's a global search
      brandingStore.globalFilters.areaFilter = newValue
    } else {
      // Update the local variable if it's not a global search
      areaFilter.value = newValue
    }
  }
})

const computedPriorityFilter = computed({
  // getter
  get() {
    return isGlobalSearch.value ? brandingStore.globalFilters.priorityFilter : priorityFilter.value
  },
  // setter
  set(newValue: any[]) {
    if (isGlobalSearch.value) {
      // Update the store property if it's a global search
      brandingStore.globalFilters.priorityFilter = newValue
    } else {
      // Update the local variable if it's not a global search
      priorityFilter.value = newValue
    }
  }
})

const backButtonLink = computed(() => {
  if (props.isWfLineItems) {
    return { path: isGlobalSearch.value ? '/projects/signs/' : `/projects/signs/${projectId.value}` }
  }
  if (props.isKopLineItems) {
    return { path: isGlobalSearch.value ? '/projects/kop/' : `/projects/kop/${projectId.value}` }
  }
  if (props.isRetailLineItems) {
    return { path: isGlobalSearch.value ? '/projects/retail/' : `/projects/retail/${projectId.value}` }
  }
  return isGlobalSearch.value ? { path: '/projects/branding/' } : { path: `/projects/branding/${projectId.value}` }
})

const getLineItemReviewLink = (lineItemId: number) => {
  const routeSearchQuery = searchQuery.value ? searchQuery.value : ''
  const routeSearchByQuery = searchByQuery.value ? searchByQuery.value : 'description'
  if (isGlobalSearch.value) {
    return {
      path: `/projects/branding/global-search-line-items/${lineItemId}`,
      query: { search: routeSearchQuery, 'search-by': routeSearchByQuery }
    }
  } else {
    return {
      path: `/projects/branding/${projectId.value}/work-orders/${workOrderId.value}/line-items/${lineItemId}`,
      query: { search: routeSearchQuery, 'search-by': routeSearchByQuery }
    }
  }
}

const syncSelectedLineItemMappings = (ids: number[]) => {
  ids.forEach(id => {
    const lineItem = lineItems.value.find((lineItem: any) => lineItem.id === id) as any
    if (lineItem) {
      // If global search - fill selectedLineItemCampaignMapping on selectedLineItemIds change
      if (isGlobalSearch.value) {
        selectedLineItemCampaignMapping.value[id] = lineItem?.campaign
        selectedLineItemCampaignNameMapping.value[id] = lineItem?.campaign_name
      }
      selectedLineItemIsOnHoldMapping.value[id] = lineItem?.is_on_hold
      selectedLineItemIsCanUserSelectMapping.value[id] = lineItem?.is_can_user_select
      selectedLineItemIsCanUserApproveMapping.value[id] = lineItem?.is_can_user_approve
      selectedLineItemFilenameMapping.value[id] = lineItem?.file_name
      selectedLineItemLineNoMapping.value[id] = lineItem?.line_no
      selectedLineItemStatusNameMapping.value[id] = lineItem?.item_status_info?.line_item_status_name || null
    }
  })
}

// Add watcher on selectedLineItemIds
watch(selectedLineItemIds, (newIds, oldIds) => {
  // Find which IDs were removed (deselected)
  const removedIds = oldIds ? oldIds.filter(id => !newIds.includes(id)) : []

  // Remove mappings only for deselected items
  removedIds.forEach(id => {
    delete selectedLineItemCampaignMapping.value[id]
    delete selectedLineItemCampaignNameMapping.value[id]
    delete selectedLineItemIsOnHoldMapping.value[id]
    delete selectedLineItemIsCanUserSelectMapping.value[id]
    delete selectedLineItemIsCanUserApproveMapping.value[id]
    delete selectedLineItemFilenameMapping.value[id]
    delete selectedLineItemLineNoMapping.value[id]
    delete selectedLineItemStatusNameMapping.value[id]
  })

  syncSelectedLineItemMappings(newIds)
}, { deep: true })

// Add computed property to check if all selected items are not is on hold
const isAllSelectedItemsIsNotOnHold = computed(() => {
  if (selectedLineItemIds.value.length === 0) return false

  // Check if all selected line items not is on hold
  return selectedLineItemIds.value.every((id: number) => selectedLineItemIsOnHoldMapping.value[id] === false)
})

// Add computed property to check if all selected items are IsCanUserSelect
const isAllSelectedItemsIsCanUserSelect = computed(() => {
  if (selectedLineItemIds.value.length === 0) return false

  // Check if all selected line items IsCanUserSelect
  return selectedLineItemIds.value.every((id: number) => selectedLineItemIsCanUserSelectMapping.value[id] === true)
})

// Add computed property to check if all selected items are IsCanUserApprove
const isAllSelectedItemsIsCanUserApprove = computed(() => {
  if (selectedLineItemIds.value.length === 0) return false

  // Check if all selected items IsCanUserApprove
  return selectedLineItemIds.value.every((id: number) => selectedLineItemIsCanUserApproveMapping.value[id] === true)
})

// Add computed property to check if all selected items are from the same campaign
const isAllSelectedItemsFromOneProject = computed(() => {
  if (!isGlobalSearch.value) return true

  if (selectedLineItemIds.value.length === 0) return false

  const firstCampaign = selectedLineItemCampaignMapping.value[selectedLineItemIds.value[0]]
  // Check if firstCampaign is null or undefined
  if (!firstCampaign) return false

  // Check if all selected line items belong to the same campaign
  return selectedLineItemIds.value.every(id => selectedLineItemCampaignMapping.value[id] === firstCampaign)
})

const firstSelectedItemProject = computed(() => {
  if (!isGlobalSearch.value) return projectId.value

  if (selectedLineItemIds.value.length === 0) return null

  const firstCampaign = selectedLineItemCampaignMapping.value[selectedLineItemIds.value[0]]
  // Check if firstCampaign is null or undefined
  if (!firstCampaign) return null

  return firstCampaign
})

const singleFilteredProjectId = computed<number | null>(() => {
  if (!isBrandingGlobalSearch.value || brandingStore.globalFilters.projectFilter.length !== 1) {
    return null
  }

  const projectIdFromFilter = Number(brandingStore.globalFilters.projectFilter[0]?.id)
  return Number.isNaN(projectIdFromFilter) ? null : projectIdFromFilter
})

const resolvedProjectForFileManagement = computed(() => {
  if (route.params.id) {
    return route.params.id
  }

  if (selectedLineItemIds.value.length && firstSelectedItemProject.value) {
    return firstSelectedItemProject.value
  }

  return singleFilteredProjectId.value
})

const isBrandingWorkOrderView = computed(() => {
  return !isGlobalSearch.value && !props.isProjectOnlyView && !props.isWfLineItems && !props.isKopLineItems && !props.isRetailLineItems
})

const projectFileScopes = computed(() => {
  if (route.params.id) {
    const projectIdValue = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
    return [{
      projectId: projectIdValue,
      projectName: computedProject.value?.name || workOrder.value?.venue || null,
      selectedItems: selectedLineItemIds.value.map(id => ({
        id,
        identity: selectedLineItemFilenameMapping.value[id] || null,
      })),
    }]
  }

  if (selectedLineItemIds.value.length) {
    const scopes = new Map<string, {
      projectId: number | string
      projectName: string | null
      selectedItems: Array<{ id: number; identity: string | null }>
    }>()

    for (const id of selectedLineItemIds.value) {
      const projectIdValue = selectedLineItemCampaignMapping.value[id]
      if (!projectIdValue) continue

      const key = String(projectIdValue)
      if (!scopes.has(key)) {
        scopes.set(key, {
          projectId: projectIdValue,
          projectName: selectedLineItemCampaignNameMapping.value[id] || null,
          selectedItems: [],
        })
      }

      scopes.get(key)?.selectedItems.push({
        id,
        identity: selectedLineItemFilenameMapping.value[id] || null,
      })
    }

    return Array.from(scopes.values())
  }

  if (singleFilteredProjectId.value) {
    return [{
      projectId: singleFilteredProjectId.value,
      projectName: brandingStore.globalFilters.projectFilter[0]?.name || null,
      selectedItems: [],
    }]
  }

  return []
})

const waitingRoomProjectOptions = computed(() => {
  return projectFileScopes.value
    .map(scope => {
      const projectId = Number(scope.projectId)
      if (Number.isNaN(projectId)) {
        return null
      }

      return {
        id: projectId,
        name: scope.projectName || `Project #${projectId}`,
      }
    })
    .filter((option): option is { id: number, name: string } => !!option)
})

// const firstSelectedItemFilename = computed(() => {
//   if (selectedLineItemIds.value.length === 0) return null
//
//   const firstFilename = selectedLineItemFilenameMapping.value[selectedLineItemIds.value[0]]
//   // Check if firstFilename is null or undefined
//   if (!firstFilename) return null
//
//   return firstFilename
// })

// // Check if all selected items have the same filename
// const isAllSelectedItemsHaveSameFilename = computed(() => {
//   // Check if firstFilename is null or undefined
//   if (!firstSelectedItemFilename.value) return false
//
//   return selectedLineItemIds.value
//     .every(id => selectedLineItemFilenameMapping.value[id] === firstSelectedItemFilename.value)
// })

// Define Line Items Campaign(Project) type
const campaignType = computed(() => {
  return props.isRetailLineItems ? 0 : (props.isWfLineItems ? 1 : (props.isKopLineItems ? 5 : 2))
})

const getCurrentFilters = (withoutSearchFilter = false) => {
  let filterProjects = [] as number[]
  let filterWorkOrders = [] as number[]

  if (brandingStore.globalFilters.projectFilter && isGlobalSearch.value) {
    filterProjects = brandingStore.globalFilters.projectFilter
      .filter((i: any) => !i?.selectedWorkOrders?.length).map((i: any) => i.id)
    const filterProjectWorkOrders = brandingStore.globalFilters.projectFilter
      .filter((i: any) => i?.selectedWorkOrders?.length).map((i: any) => i.selectedWorkOrders)
    filterWorkOrders = uniq(flattenDeep(filterProjectWorkOrders))
  }
  const filters = {
    work_order__campaign__type: campaignType.value,
    work_order__campaign__is_visible: isBrandingGlobalSearch.value ? isActiveProjectsFilter.value : undefined,
    visibility__in: getCampaignItemVisibilityFilter(!!hiddenVisibilityFilter.value),
    priority__in: computedPriorityFilter.value.length ? computedPriorityFilter.value.map(i => i.value) : undefined,
    work_order__in: filterWorkOrders?.length ? filterWorkOrders : undefined,
    work_order__campaign__in: filterProjects?.length ? filterProjects : undefined,
    work_order__campaign__suppliers__in: brandingStore.globalFilters?.supplierFilter?.length &&
    isGlobalSearch.value ? brandingStore.globalFilters.supplierFilter.map((i: any) => i.id) : undefined,
    responsible_list__in: computedResponsibleFilter.value?.length ? computedResponsibleFilter.value.map((i: any) => i.id) : undefined,
    assigned_to_list__in: computedAssignedToFilter.value?.length ? computedAssignedToFilter.value.map((i: any) => i.id) : undefined,
    item_status__in: computedStatusFilter.value?.length ? computedStatusFilter.value.map((i: any) => i.id) : undefined,
    zone__in: computedZoneFilter.value?.length ? computedZoneFilter.value : undefined,
    area__in: computedAreaFilter.value?.length ? computedAreaFilter.value : undefined,
    due_date__gte: dueDateStart.value,
    due_date__lte: dueDateEnd.value,
    work_order__campaign__event_group_or_children__in: brandingStore.globalFilters?.eventGroupFilter?.length &&
    isGlobalSearch.value ? brandingStore.globalFilters.eventGroupFilter.map((i: any) => i.id) : undefined,
    is_on_hold: isOnHoldFilter.value !== null ? isOnHoldFilter.value : undefined,
    is_released: isReleasedFilter.value,
    is_rejected_back_status: rejectedItemsOnlyFilter.value !== null ? true : undefined,
    work_order__category_1__in: brandingStore.globalFilters?.category1Filter?.length && isGlobalSearch.value ?
      brandingStore.globalFilters.category1Filter.map((i: any) => i.value) : undefined,
    work_order__category_2__in: brandingStore.globalFilters?.category2Filter?.length && isGlobalSearch.value ?
      brandingStore.globalFilters.category2Filter.map((i: any) => i.value) : undefined,
    supplier_erp__in: supplierErpFilter.value?.length ? supplierErpFilter.value : undefined,
    // get_all: true,
    is_global_search: isGlobalSearch.value
  } as any
  if (!withoutSearchFilter) {
    filters[`${searchBy.value}__icontains`] = lineItemSearch.value
  }
  for (const key of Object.keys(filters)) {
    if (!filters[key] && filters[key] !== 0 && !['is_on_hold', 'work_order__campaign__is_visible'].includes(key)) {
      delete filters[key]
    }
  }
  return filters
}

const getWorkOrder = async () => {
  workOrderLoading.value = true
  try {
    const res = await $workOrder(workOrderId.value)
    workOrder.value = res.data

    await getGlobalLineItemOptions()
  } catch (e: any) {
    console.log(e)
  } finally {
    workOrderLoading.value = false
  }
}

const getProject = async () => {
  try {
    const res = await $getCampaign(route.params.id)
    project.value = res.data
  } catch (e) {
    console.log(e)
  }
}

const getLineItemListFiltersPayload = () => {
  // Define default ordering
  let ordering = ''

  // Check if sortBy is defined and has at least one item
  if (computedLineItemTableOptions.value.sortBy.length > 0) {
    const sortBy = computedLineItemTableOptions.value.sortBy[0] as TableSorting
    const sortByKey = sortBy.key
    const sortOrder = sortBy.order === 'desc' ? '-' : '' // Determine the sorting order

    // Map 'unresolved_annotations_count' as is and other keys as 'key, line_no'
    ordering = sortByKey === 'unresolved_annotations_count' ? sortByKey : `${sortByKey}, line_no`

    ordering = `${sortOrder}${ordering}`
  }

  let filterProjects = [] as number[]
  let filterWorkOrders = [] as number[]

  if (brandingStore.globalFilters.projectFilter && isGlobalSearch.value) {
    filterProjects = brandingStore.globalFilters.projectFilter.filter((i: any) => !i?.selectedWorkOrders?.length).map((i: any) => i.id)
    const filterProjectWorkOrders = brandingStore.globalFilters.projectFilter.filter((i: any) => i?.selectedWorkOrders?.length).map((i: any) => i.selectedWorkOrders)
    filterWorkOrders = uniq(flattenDeep(filterProjectWorkOrders))
  }

  const payload = {
    work_order__campaign__type: campaignType.value,
    work_order__campaign__is_visible: isBrandingGlobalSearch.value ? isActiveProjectsFilter.value : undefined,
    visibility__in: getCampaignItemVisibilityFilter(!!hiddenVisibilityFilter.value).join(','),
    work_order: workOrderId.value ? workOrderId.value : undefined,
    work_order__in: filterWorkOrders?.length ? filterWorkOrders : undefined,
    work_order__campaign: props.isProjectOnlyView ? projectId.value : undefined,
    work_order__campaign__in: filterProjects?.length ? filterProjects : undefined,
    work_order__campaign__suppliers__in: brandingStore.globalFilters?.supplierFilter?.length &&
    isGlobalSearch.value ? brandingStore.globalFilters.supplierFilter.map((i: any) => i.id).join(',') : undefined,
    work_order__campaign__event_group_or_children__in: brandingStore.globalFilters?.eventGroupFilter?.length &&
    isGlobalSearch?.value ? brandingStore.globalFilters.eventGroupFilter.map((i: any) => i.id).join(',') : undefined,
    work_order__category_1__in: brandingStore.globalFilters?.category1Filter?.length && isGlobalSearch.value ?
      brandingStore.globalFilters.category1Filter.map((i: any) => i.value).join(',') : undefined,
    work_order__category_2__in: brandingStore.globalFilters?.category2Filter?.length && isGlobalSearch.value ?
      brandingStore.globalFilters.category2Filter.map((i: any) => i.value).join(',') : undefined,
    supplier_erp__in: supplierErpFilter.value?.length ? supplierErpFilter.value : undefined,
    page: computedLineItemTableOptions.value.page,
    per_page: computedLineItemTableOptions.value.itemsPerPage,
    ordering: ordering,
    responsible_list__in: computedResponsibleFilter.value?.length ? computedResponsibleFilter.value.map((i: any) => i.id ? i.id : 'null').join(',')
      : undefined,
    assigned_to_list__in: computedAssignedToFilter.value?.length ? computedAssignedToFilter.value.map((i: any) => i.id ? i.id : 'null').join(',')
      : undefined,
    item_status__in: computedStatusFilter.value?.length ? computedStatusFilter.value.map((i: any) => i.id).join(',')
      : undefined,
    zone__in: computedZoneFilter.value?.length ? computedZoneFilter.value.join(',') : undefined,
    area__in: computedAreaFilter.value?.length ? computedAreaFilter.value.join(',') : undefined,
    due_date__gte: dueDateStart.value,
    due_date__lte: dueDateEnd.value,
    priority__in: computedPriorityFilter.value.length ? computedPriorityFilter.value.map(i => i.value).join(',') : undefined,
    is_released: isReleasedFilter.value,
    is_rejected_back_status: rejectedItemsOnlyFilter.value !== null ? true : undefined,
    is_on_hold: isOnHoldFilter.value !== null ? isOnHoldFilter.value : undefined,
    is_global_search: isGlobalSearch.value,
  } as any

  if (lineItemSearch.value && searchBy.value) {
    payload[`${searchBy.value}__icontains`] = lineItemSearch.value
  }

  return payload
  // remaining
  // action: this.filteringAction,
  // get_all: !this.isMyItems,
}

let abortController: AbortController | null = null
let globalOptionsAbortController: AbortController | null = null

const getLineItems = async (): Promise<void> => {
  if (isDisposed) return

  abortController?.abort()
  const controller = new AbortController()
  abortController = controller
  const isCurrentRequest = () => abortController === controller && !controller.signal.aborted

  lineItemsLoading.value = true

  const payload = getLineItemListFiltersPayload()

  try {
    const res = await $lineItemList(payload, {
      signal: controller.signal
    })

    if (!isCurrentRequest()) return

    lineItems.value = res.data.results.map((i: any) => ({ ...i, selected: false }))
    lineItemsCount.value = res.data.count
    syncSelectedLineItemMappings(selectedLineItemIds.value)

  } catch (e: any) {
    if (!isCurrentRequest()) return

    // If page doesn't exist goes to 1 page
    if (e?.response?.status === 404 && computedLineItemTableOptions.value.page > 1) {
      computedLineItemTableOptions.value.page = 1
      await getLineItems()
    } else if (e.name !== 'AbortError') {
      console.log(e)
    }
  } finally {
    if (isCurrentRequest()) {
      lineItemsLoading.value = false
    }
  }
}

const unsubscribeWaitingRoomAutoSyncRefresh = onWaitingRoomAutoSyncRefresh(result => {
  if (isMounted.value && result.affected_targets.some(target => target.target_type === 'line_item')) {
    void getLineItems()
  }
})

const getGlobalLineItemOptions = async () => {
  if (isDisposed) return

  globalOptionsAbortController?.abort()
  const controller = new AbortController()
  globalOptionsAbortController = controller
  const isCurrentRequest = () => globalOptionsAbortController === controller && !controller.signal.aborted
  globalOptionsLoading.value = true

  const optionsFilters = { ...getCurrentFilters(true) }
  if (!isGlobalSearch.value) {
    if (props.isProjectOnlyView) {
      optionsFilters.work_order__campaign = projectId.value
    } else {
      optionsFilters.work_order = workOrderId.value
    }
  }

  // 0 - Retail, 1 - Wayfinding, 2 - Branding, 5 - KOP
  const optionsProjectType = props.isRetailLineItems ? 0 : (props.isWfLineItems ? 1 : (props.isKopLineItems ? 5 : 2))
  try {
    const res = await $brandingGlobalLineItemOptions(
      {
        filters: JSON.stringify(optionsFilters),
        options_campaign_types: JSON.stringify([optionsProjectType])
      },
      { signal: controller.signal }
    )

    if (!isCurrentRequest()) return

    projectOptions.value = res.data?.campaign_options || []
    supplierOptions.value = res.data?.supplier_options || []
    programOptions.value = res.data?.event_group_options || []
    category1Options.value = res.data?.category_1_options || []
    category2Options.value = res.data?.category_2_options || []
    responsibleOptions.value = res.data?.responsible_options || []
    assignedToOptions.value = res.data?.assigned_to_options || []
    statusOptions.value = res.data?.status_options || []
    zoneOptions.value = res.data?.zone_options || []
    areaOptions.value = res.data?.area_options || []
    isOnHoldOptions.value = res.data?.on_hold_options || [true, false]
    supplierErpOptions.value = res.data?.supplier_erp_options || []
    priorityOptions.value = res.data?.priority_options || []
  } catch (e: any) {
    if (isCurrentRequest()) {
      console.log(e)
    }
  } finally {
    if (isCurrentRequest()) {
      globalOptionsLoading.value = false
    }
  }
}

const onZoneOptionsFocus = () => {
  if (!zoneOptions.value.length) {
    getGlobalLineItemOptions()
  }
}
const onAreaOptionsFocus = () => {
  if (!areaOptions.value.length) {
    getGlobalLineItemOptions()
  }
}

const onResponsibleOptionsFocus = () => {
  if (!responsibleOptions.value.length) {
    getGlobalLineItemOptions()
  }
}

const onAssignedToOptionsFocus = () => {
  if (!assignedToOptions.value.length) {
    getGlobalLineItemOptions()
  }
}

const onStatusOptionsFocus = () => {
  if (!statusOptions.value.length) {
    getGlobalLineItemOptions()
  }
}

const onPriorityOptionsFocus = () => {
  if (!priorityOptions.value.length) {
    getGlobalLineItemOptions()
  }
}

// Add new method to handle reject status selection
const handleRejectStatusSelect = async () => {
  if (!selectedRejectStatus.value) return

  await multipleApproveRejectLineItems(false, selectedRejectStatus.value)
  showRejectStatusDialog.value = false
  selectedRejectStatus.value = null
}

// Update the multipleApproveRejectLineItems function to accept reject_status_id
const multipleApproveRejectLineItems = async (isApprove: boolean, reject_status_id = null) => {
  isApprove ? approveLoading.value = true : rejectLoading.value = true
  const payload = {
    is_approve: isApprove,
    line_item_ids: selectedLineItemIds.value
  } as any

  // Add reject_status_id to payload if provided
  if (!isApprove && reject_status_id) {
    payload.reject_status_id = reject_status_id
  }

  try {
    waitingRoomAutoSyncStore.handleResponse(await $multipleApproveLineItem(payload))
    selectedLineItemIds.value = []
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item(s) updated successfully!'
    })

    // // Hide Approve Reject Dialog if visible
    // if (showLineItemMultipleApproveRejectDialog.value) showLineItemMultipleApproveRejectDialog.value = false

    await getLineItems()
  } catch (e: any) {
    const mes = e?.response?.data?.message ? e?.response?.data?.message : (e?.response?.data ? JSON.stringify(e?.response?.data) : e?.message)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    approveLoading.value = false
    rejectLoading.value = false
  }
}

// Add method to fetch reject status options
const getRejectStatusOptions = async () => {
  try {
    // Assuming first selected line item's ID is used to get workflow statuses
    const firstLineItemId = selectedLineItemIds.value[0]
    const response = await $getLineItemWfPreviousStatuses(firstLineItemId)
    rejectStatusOptions.value = response.data
  } catch (e: any) {
    console.error('Error fetching reject statuses:', e)
    rejectStatusOptions.value = []
  }
}

const multipleUpdateLineItemAssignee = async (assignee: any) => {
  itemAssigneeDialogLoading.value = true
  const payload = {
    assignee_type: selectedAssigneeType.value,  // 'responsible' or 'assigned_to'
    user_id: assignee ? assignee.id : null,
    line_item_ids: selectedLineItemIds.value
  }
  try {
    await $multipleUpdateLineItemAssignee(payload)
    selectedLineItemIds.value = []
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item(s) updated successfully!'
    })
    showItemAssigneeDialog.value = false
    await getLineItems()
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    itemAssigneeDialogLoading.value = false
  }
}

const multipleUpdateLineItemOnHold = async (isOnHold: boolean) => {
  updateBtnLoading.value = true
  const payload = {
    is_on_hold: isOnHold,
    line_item_ids: selectedLineItemIds.value
  }
  try {
    await $multipleUpdateLineItemOnHold(payload)
    selectedLineItemIds.value = []
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item(s) updated successfully!'
    })
    await getLineItems()
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    updateBtnLoading.value = false
  }
}

// Function to update item visibility with financial inclusion/exclusion options
const multipleUpdateItemVisibility = async (selectedVisibility: number) => {
  updateBtnLoading.value = true

  const payload = {
    visibility: selectedVisibility,
    line_item_ids: selectedLineItemIds.value
  }
  try {
    await $multipleUpdateLineItemVisibility(payload)
    selectedLineItemIds.value = []
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item(s) visibility updated successfully!'
    })
    await getLineItems()
  } catch (e: any) {
    const mes = e?.response?.data ? JSON.stringify(e?.response?.data) : e?.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    updateBtnLoading.value = false
  }
}

// silently update the query without causing a page reload, onMounted and navigation guard methods
const updateSearchQuery = async () => {
  await setQuery('search', lineItemSearch.value?.toString() || undefined)
  await setQuery('search-by', searchBy.value?.toString() || undefined)
}

const onLineItemSearch = debounce(async () => {
  await updateSearchQuery()
  // Clear selected items
  selectedLineItemIds.value = []
  await getLineItems()
}, 500)

const onUpdateTableOptions = async ({ page, itemsPerPage, sortBy }: TableOptions) => {
  computedLineItemTableOptions.value.page = page
  computedLineItemTableOptions.value.itemsPerPage = itemsPerPage
  computedLineItemTableOptions.value.sortBy = sortBy
  if (isMounted.value) {
    await getLineItems()
  }
}

const artworkDownloadingStats = ref<ArtworkDownloadUI | null>(null)

const getLineItemArtworkExportPayload = () => {
  if (selectedLineItemIds.value?.length) {
    return {
      id__in: selectedLineItemIds.value.join(','),
    }
  }

  return {
    ...getLineItemListFiltersPayload(),
    page: 1,  // First page only
    per_page: -1  // All filtered items without limit
  }
}

const getLineItemArtworkExportItems = (data: any): any[] => {
  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data?.results)) {
    return data.results
  }

  return []
}

const getLineItemArtworkDownloadTargets = (items: any[]): FreshDownloadTarget[] => {
  return items
    .flatMap((item) => Array.isArray(item?.artwork_link_list) ? item.artwork_link_list : [])
    .filter((target: any) => target?.item_file_id && target?.file_name && target?.campaign_id)
    .map((target: any) => ({
      projectId: target.campaign_id,
      fileId: target.item_file_id,
      filename: target.file_name,
    }))
}

const showLineItemArtworkDownloadResult = (total: number, triggered: number) => {
  if (total === triggered) {
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Download Complete',
      text: `All ${total} file(s) downloaded successfully!`
    })
  }
  else if (!!triggered && total !== triggered) {
    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert',
      title: 'Partial Download',
      text: `${triggered} of ${total} file(s) downloaded (${(total || 0) - (triggered || 0)} failed)`
    })
  }
  else {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: 'Failed to process download request'
    })
  }
}

const handleLineItemArtworkDownloadProgress = (event: DownloadProgressEvent) => {
  const { triggered, total, inProgress, remaining } = event.stats

  artworkDownloadingStats.value = {
    active: true,
    triggered,
    total,
    inProgress,
    remaining
  }

  if (!remaining) {
    showLineItemArtworkDownloadResult(total, triggered)
    artworkDownloadingStats.value = null
  }
}

const downloadArtworkFiles = async () => {
  exportLoading.value = true

  try {
    const res = await $getBrandingItemArtworkLinkList(getLineItemArtworkExportPayload())
    const lineItemArtworks = getLineItemArtworkDownloadTargets(getLineItemArtworkExportItems(res.data))

    if (!lineItemArtworks.length) {
      snackbarStore.showMessage({
        color: 'warning',
        icon: 'mdi-alert-circle',
        title: 'Warning',
        text: 'No valid artwork links found'
      })
      return
    }

    await downloadFreshFiles(
      lineItemArtworks,
      handleLineItemArtworkDownloadProgress,
    )
  } catch (e) {
    console.log(e)

    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Failed to download file(s)'
    })
  } finally {
    artworkDownloadingStats.value = null
    exportLoading.value = false
    showBrandingExportMenu.value = false
  }
}

const exportExcelLineItems = async () => {
  exportLoading.value = true

  const excludedHeadersForExcel = ['action', 'select', 'approve_reject_status', 'view']

  const updatedFilters = {
    ...getCurrentFilters()
  }
  if (!isGlobalSearch.value) {
    if (props.isProjectOnlyView) {
      updatedFilters.work_order__campaign = projectId.value
    } else {
      updatedFilters.work_order__campaign = projectId.value
      updatedFilters.work_order = workOrderId.value
    }
  }

  const payload = {
    export_categories: ['excel'],
    columns: lineItemHeaders.value.filter((i: any) => i.align === '' && !excludedHeadersForExcel.includes(i.key))
      .map((i: any) => ({ title: i.title, key: i.key })),
    filters: JSON.stringify(updatedFilters),
    selected_item_ids: selectedLineItemIds.value
  } as any

  try {
    // Export Excel/Artwork Branding Line Items from Global Search/Work Order View
    const task = await $exportBrandingItems(payload)
    if (task.data?.task_id) {
      await taskPolling(task.data?.task_id)
    }

    // Clear selected
    selectedLineItemIds.value = []
  } catch (e: any) {
    console.log(e)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Oops, something went wrong, try again'
    })
  }
}

const taskPolling = async (task_id: string) => {
  const interval = setInterval(async () => {
    const res = await $taskPolling(task_id)
    if (res.data?.status === 'SUCCESS') {
      clearInterval(interval)
      const link = document.createElement('a')
      link.href = res.data.result
      link.setAttribute('download', 'Items')
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(res.data.result)
      document.body.removeChild(link)
      exportLoading.value = false
    }
  }, 5000)
}

const exportBrandingItems = async () => {
  showBrandingExportMenu.value = false

  // If artwork is selected, use download function
  if (selectedExportCategories.value.includes('artwork')) {
    await downloadArtworkFiles()
  }

  // If excel category selected, use backend download function
  if (selectedExportCategories.value.includes('excel')) {
    await exportExcelLineItems()
  }
}

const openSharedUploadLinkDialog = () => {
  showSharedUploadLinkDialog.value = true
}

const copySharedUploadLink = async () => {
  try {
    await navigator.clipboard.writeText(sharedUploadLink.value)

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Copied',
      text: 'Shared upload link copied to clipboard'
    })
  } catch (e) {
    console.log(e)

    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert-circle',
      title: 'Copy Link',
      text: 'Unable to copy automatically. Select and copy the link manually.'
    })
  }
}

const onUpdateTableColumns = (event: TableHeader[]) => {
  brandingStore.updateLineItemHeaders(event)
}

const updateStoreFilters = () => {
  // Update previous revisions store data
  // Update eventGroupFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.eventGroupFilter)) {
    brandingStore.globalFilters.eventGroupFilter = []
  }

  // Update supplierFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.supplierFilter)) {
    brandingStore.globalFilters.supplierFilter = []
  }

  // Update category1Filter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.category1Filter)) {
    brandingStore.globalFilters.category1Filter = []
  }

  // Update category2Filter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.category2Filter)) {
    brandingStore.globalFilters.category2Filter = []
  }

  // Update responsibleFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.responsibleFilter)) {
    brandingStore.globalFilters.responsibleFilter = []
  }

  // Update assignedToFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.assignedToFilter)) {
    brandingStore.globalFilters.assignedToFilter = []
  }

  // Update statusFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.statusFilter)) {
    brandingStore.globalFilters.statusFilter = []
  }

  // Update zoneFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.zoneFilter)) {
    brandingStore.globalFilters.zoneFilter = []
  }

  // Update areaFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.areaFilter)) {
    brandingStore.globalFilters.areaFilter = []
  }

  // Update priorityFilter if it's not already an array
  if (!Array.isArray(brandingStore.globalFilters.priorityFilter)) {
    brandingStore.globalFilters.priorityFilter = []
  }
}

const clearGlobalFilters = () => {
  supplierErpFilter.value = []

  if (isGlobalSearch.value) {
    brandingStore.globalFilters = {
      projectFilter: [],
      supplierFilter: [],
      eventGroupFilter: [],
      category1Filter: [],
      category2Filter: [],
      responsibleFilter: [],
      assignedToFilter: [],
      statusFilter: [],
      zoneFilter: [],
      areaFilter: [],
      priorityFilter: []
    }
  }
}

const onUpdateFilters = () => {
  // Clear selected items
  selectedLineItemIds.value = []
  getGlobalLineItemOptions()
}

// silently update the query without causing a page reload, onMounted and navigation guard methods
const deleteClearFiltersQuery = async () => {
  await setQuery('clear_filters', undefined)
}

// Add new refs for reject dialog
const showRejectStatusDialog = ref<boolean>(false)
const rejectStatusOptions = ref([])
const selectedRejectStatus = ref(null)

const setupBrandingItemListPageInitialParams = async () => {
  updateStoreFilters()

  if (route.query.clear_filters) {
    // Clear global(store) filters to get the expected result
    clearGlobalFilters()
    // Delete 'clear_filters' query param
    await deleteClearFiltersQuery()
  } else {
    await updateFiltersFromQuery()
  }

  hasInitializedFilters = true
  await getGlobalLineItemOptions()
}

onMounted(async () => {
  // Get available columns based on user divisions
  await divisionColumnsStore.fetchUserAvailableColumns([COLUMN_TYPE_BRANDING])

  await setupBrandingItemListPageInitialParams()

  await getLineItems()

  if (!isGlobalSearch.value && props.isProjectOnlyView) {
    if (!props.currentProject) {
      await getProject()
    }
  }
  else if (!isGlobalSearch.value) {
    await getWorkOrder()
  }

  isMounted.value = !isDisposed
})

onBeforeUnmount(() => {
  isDisposed = true
  hasInitializedFilters = false
  activeProjectsRefreshId++
  abortController?.abort()
  globalOptionsAbortController?.abort()
  onLineItemSearch.cancel()
  unsubscribeWaitingRoomAutoSyncRefresh()
})

const parseQueryArray = (queryValue: string | LocationQueryValue | LocationQueryValue[] | undefined): any[] => {
  if (!queryValue) return []
  if (Array.isArray(queryValue)) return queryValue

  try {
    return JSON.parse(queryValue) || []
  } catch {
    return []
  }
}

const updateFiltersFromQuery = async () => {
  // Clear global(store) filters to get the expected result
  clearGlobalFilters()
  supplierErpFilter.value = []

  // Update filters from query
  lineItemSearch.value = !!searchQuery.value && typeof searchQuery.value === 'string' ? searchQuery.value : ''
  searchBy.value = !!searchByQuery.value && typeof searchByQuery.value === 'string' ? searchByQuery.value : 'description'

  if (isGlobalSearch.value) {
    // Update filters from query
    computedStatusFilter.value = parseQueryArray(route.query.item_statuses)
    brandingStore.globalFilters.projectFilter = parseQueryArray(route.query.projects)
    brandingStore.globalFilters.supplierFilter = parseQueryArray(route.query.suppliers)
    brandingStore.globalFilters.eventGroupFilter = parseQueryArray(route.query.programs)
    brandingStore.globalFilters.category1Filter = parseQueryArray(route.query.categories_1)
    brandingStore.globalFilters.category2Filter = parseQueryArray(route.query.categories_2)
    supplierErpFilter.value = parseQueryArray(route.query.supplier_erp).map((i: any) => `${i}`)
    isReleasedFilter.value = route.query.is_released_items ? true : null
    computedResponsibleFilter.value = parseQueryArray(route.query.responsible)
    computedAssignedToFilter.value = parseQueryArray(route.query.assigned_to)
    computedZoneFilter.value = parseQueryArray(route.query.zones)
    computedAreaFilter.value = parseQueryArray(route.query.areas)
    computedPriorityFilter.value = parseQueryArray(route?.query?.priority__in)
    dueDateStart.value = route.query.due_date_start && typeof route.query.due_date_start === 'string' ? route.query.due_date_start : null
    dueDateEnd.value = route.query.due_date_end && typeof route.query.due_date_end === 'string' ? route.query.due_date_end : null
    isOnHoldFilter.value = route.query.is_on_hold && ['true', 'false'].includes(route.query?.is_on_hold?.toString() || '') ? route.query?.is_on_hold === 'true' : null
    rejectedItemsOnlyFilter.value = route.query?.is_rejected_items_only === 'true' ? true : null

    if (can('view','hiddenlineitem')) {
      hiddenVisibilityFilter.value = route.query.is_hidden ? route.query.is_hidden === 'true' : false
    } else {
      // Reset is_hidden query if no access
      hiddenVisibilityFilter.value = false
      await setQuery('is_hidden', undefined)
    }
  }
}

watch(() => route.query.is_active_projects, async () => {
  if (!isBrandingGlobalSearch.value || isDisposed) return

  const refreshId = ++activeProjectsRefreshId
  abortController?.abort()
  globalOptionsAbortController?.abort()
  onLineItemSearch.cancel()
  selectedLineItemIds.value = []
  lineItems.value = []
  lineItemsCount.value = 0
  computedLineItemTableOptions.value.page = 1
  projectOptions.value = []
  supplierOptions.value = []
  programOptions.value = []
  category1Options.value = []
  category2Options.value = []
  responsibleOptions.value = []
  assignedToOptions.value = []
  statusOptions.value = []
  zoneOptions.value = []
  areaOptions.value = []
  isOnHoldOptions.value = []
  supplierErpOptions.value = []
  priorityOptions.value = []

  // Read the entire incoming query so linked filter selections remain intact.
  await updateFiltersFromQuery()
  if (!hasInitializedFilters || isDisposed || refreshId !== activeProjectsRefreshId) return

  await Promise.all([getLineItems(), getGlobalLineItemOptions()])
})

const handleActiveProjectsFilterChange = async (isActive: boolean) => {
  if (!isBrandingGlobalSearch.value || isActive === isActiveProjectsFilter.value) return

  onLineItemSearch.cancel()
  await customUpdateQuery(query => {
    // Project/Work Order selections share the projects query parameter.
    const dependentQueryKeys = [
      'projects', 'programs', 'suppliers', 'categories_1', 'categories_2',
      'responsible', 'assigned_to', 'item_statuses', 'zones', 'areas',
      'priority__in', 'supplier_erp', 'due_date_start', 'due_date_end',
      'is_on_hold', 'is_released_items', 'is_rejected_items_only', 'is_hidden',
      'page', 'clear_filters'
    ]
    for (const key of dependentQueryKeys) {
      delete query[key]
    }
    if (lineItemSearch.value) {
      query.search = lineItemSearch.value
    } else {
      delete query.search
    }
    query['search-by'] = searchBy.value
    query.is_active_projects = String(isActive)
  })
}

const handleApproveRejectItemsClick = (isApprove: boolean) => {
  // // Items with Same Filename handling
  // if (isAllSelectedItemsHaveSameFilename && firstSelectedItemFilename.value) {
  //   showLineItemMultipleApproveRejectDialog.value = true
  //   isApproveLineItemMultipleApproveRejectDialog.value = isApprove
  //   lineItemMultipleApproveRejectDialogFilters.value = getLineItemListFiltersPayload()
  // }
  // // Base handling
  // else {
  // Approve
  if (isApprove) {
    multipleApproveRejectLineItems(true)
  }
  // Reject
  else {
    getRejectStatusOptions()
    showRejectStatusDialog.value = true
  }
  // }
}

const handleApproveItemsClick = () => {
  handleApproveRejectItemsClick(true)
}

const handleRejectItemsClick = () => {
  handleApproveRejectItemsClick(false)
}

const handleOnHoldFilterChange = (
  { filterValue, queryValue }: { filterValue: boolean | null, queryValue: 'true' | 'false' | undefined }
) => {
  isOnHoldFilter.value = filterValue
  getLineItems()
  onUpdateFilters()
  setQuery('is_on_hold', queryValue)
}

const handleReleasedFilterChange = (filterValue: true | null) => {
  isReleasedFilter.value = filterValue
  getLineItems()
  onUpdateFilters()
  setQuery('is_released_items', filterValue ? 'true' : undefined)

}

const handleHiddenVisibilityFilterChange = (filterValue: boolean) => {
  hiddenVisibilityFilter.value = filterValue
  getLineItems()
  onUpdateFilters()
  setQuery('is_hidden', filterValue ? 'true' : undefined)
}

const handleRejectedItemsOnlyFilterChange = (filterValue: true | null) => {
  rejectedItemsOnlyFilter.value = filterValue
  getLineItems()
  onUpdateFilters()
  setQuery('is_rejected_items_only', filterValue ? 'true' : undefined)
}
</script>

<template>
  <div class="branding-line-item-list-container">
    <LineItemReviewDialog
      v-model="showLineItemReviewDialog"
      :project-id="selectedLineItemForReviewDialog.campaign_id"
      :work-order-id="selectedLineItemForReviewDialog.work_order_id"
      :line-item-id="selectedLineItemForReviewDialog.id"
      @onApprovalAction="getLineItems()"
      @uploaded="getLineItems()"
    />
<!--    <LineItemMultipleApproveRejectDialog-->
<!--      v-model="showLineItemMultipleApproveRejectDialog"-->
<!--      v-model:base-selected-line-item-ids="selectedLineItemIds"-->
<!--      v-model:show-reject-status-dialog="showRejectStatusDialog"-->
<!--      :line-item-filename="firstSelectedItemFilename"-->
<!--      :is-approve="isApproveLineItemMultipleApproveRejectDialog"-->
<!--      :line-item-filters="lineItemMultipleApproveRejectDialogFilters"-->
<!--      :get-reject-status-options="getRejectStatusOptions"-->
<!--      :multiple-approve-reject-line-items="multipleApproveRejectLineItems"-->
<!--    />-->
    <UpdateWorkOrderDialog
      v-model="showUpdateWorkOrderDialog"
      :work-order-id="workOrderId"
      @onSubmit="getWorkOrder"
    />
    <ShareItemsByEmailDialog
      v-model="showShareItemsByEmailDialog"
      item-type="branding"
      :selected-item-ids="selectedLineItemIds"
      @onSubmit="selectedLineItemIds = []; getLineItems()"
    />
    <TableColumnSettingsDialog
      v-model="showTableColumnSettingsDialog"
      :headers="lineItemHeaders"
      :default-headers="defaultLineItemHeaders"
      @onUpdate="onUpdateTableColumns"
    />
    <UpdateItemStatusDialog
      v-model="showItemStatusDialog"
      :campaign-type="'branding'"
      :selected-campaign-id="firstSelectedItemProject"
      :selected-item-ids="selectedLineItemIds"
      @onSubmit="selectedLineItemIds = []; getLineItems()"
    />
    <UpdateItemPriorityDialog
      v-model="showItemPriorityDialog"
      :campaign-type="'branding'"
      :selected-item-ids="selectedLineItemIds"
      @onSubmit="selectedLineItemIds = []; getLineItems()"
    />
    <UpdateItemExtRefNumDialog
      v-model="showItemExtRefNumDialog"
      :campaign-type="'branding'"
      :selected-item-ids="selectedLineItemIds"
      @onSubmit="selectedLineItemIds = []; getLineItems()"
    />
    <!-- Assignee - responsible/assigned_to -->
    <UpdateAssigneeDialog
      v-model="showItemAssigneeDialog"
      :assignee-type="selectedAssigneeType"
      :loading="itemAssigneeDialogLoading"
      :current-assignee="null"
      :campaign-id="firstSelectedItemProject"
      @onUpdate="multipleUpdateLineItemAssignee"
    />
    <!-- /Assignee - responsible/assigned_to -->
    <ItemStatusHistoryDialog
      v-model="showLineItemStatusHistoryDialog"
      :item-id="selectedHistoryLineItemId"
      :item-type="'branding'"
      :isLineItem="true"

      :code="selectedHistoryLineItemId ? lineItems.find(i => i.id === selectedHistoryLineItemId)?.brand_book_code : ''"
    />
    <GlobalChatDialog
      v-model="showGlobalChatDialog"
      :item-id="selectedChatLineItemId"
      item-type="branding"
    />
    <ProjectFilesUploadDialog
      v-model="showProjectFilesUploadDialog"
      :project-id="resolvedProjectForFileManagement || null"
      :selected-item-ids="selectedLineItemIds.length ? selectedLineItemIds : null"
      :expected-items="selectedUploadExpectedItems"
      :is-global-upload="isBrandingGlobalSearch && !resolvedProjectForFileManagement"
      :projectTypeId="2"
      :waiting-room-project-options="waitingRoomProjectOptions"
      @uploaded="getLineItems"
    />
    <ProjectFilesDialog
      v-model="showProjectFilesDialog"
      :project-id="resolvedProjectForFileManagement || null"
      :project-scopes="projectFileScopes"
      :project-type-id="2"
    />
    <VDialog
      v-model="showSharedUploadLinkDialog"
      max-width="760"
    >
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>Shared Upload Link</span>
          <VBtn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showSharedUploadLinkDialog = false"
          />
        </VCardTitle>
        <VCardText>
          <div class="text-subtitle-2 mb-2">
            Selected items
          </div>
          <VTable
            density="compact"
            class="shared-upload-items-table mb-4"
          >
            <thead>
              <tr>
                <th class="text-left">
                  Line No
                </th>
                <th class="text-left">
                  Filename
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in selectedSharedUploadItems"
                :key="item.id"
              >
                <td>{{ item.lineNo }}</td>
                <td>{{ item.filename }}</td>
              </tr>
            </tbody>
          </VTable>
          <div class="text-subtitle-2 mb-2">
            Share link
          </div>
          <div class="d-flex align-center flex-wrap">
            <VTextField
              :model-value="sharedUploadLink"
              readonly
              density="compact"
              hide-details
              class="shared-upload-link-field mr-2"
            />
            <VBtn
              color="primary"
              variant="flat"
              size="small"
              @click="copySharedUploadLink"
            >
              <VIcon
                start
                icon="tabler-copy"
              />
              Copy
            </VBtn>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="showSharedUploadLinkDialog = false"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
    <VCard>
      <VOverlay
        v-model="workOrderLoading"
        persistent
        absolute
        contained
        class="align-center justify-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VOverlay>
      <VContainer fluid>
        <VRow>
          <VCol class="py-0 d-flex align-center justify-space-between">
            <VRow>
              <VCol
                cols="12"
                sm=""
                class="d-flex align-center justify-start"
              >
                <VBtn
                  v-if="!isLinkCreatedUser"
                  color="light"
                  size="small"
                  class="px-0 text-decoration-none text-high-emphasis"
                  :to="backButtonLink"
                >
                  <VIcon
                    start
                    icon="mdi-arrow-left"
                  />
                  Back
                </VBtn>
              </VCol>
              <VCol
                v-if="!isGlobalSearch && isProjectOnlyView"
                class="d-flex align-center justify-start justify-sm-center"
              >
                <h4>
                  {{ computedProject?.name || '' }}
                </h4>
              </VCol>
              <VCol class="d-flex align-center justify-end flex-wrap flex-sm-row">
                <div>
                  <VBtnToggle
                    v-if="selectedProjectType"
                    :model-value="selectedProjectType"
                    class="ma-1"
                    color="primary"
                    divided
                    mandatory
                    variant="outlined"
                    style="max-height: 30px"
                    @update:modelValue="newValue => emit('update:selectedProjectType', newValue)"
                  >
                    <VBtn
                      v-for="item in projectTypesList"
                      :key="item.title"
                      :value="item.value"
                    >
                      <span class="text-wrap">{{ item.title }}</span>
                    </VBtn>
                  </VBtnToggle>
                </div>
                <VMenu
                  v-if="!isLinkCreatedUser"
                  v-model="showBrandingExportMenu"
                  :close-on-back="true"
                  :close-on-content-click="false"
                  :open-on-click="true"
                >
                  <template #activator="{ props }">
                    <div>
                      <VMenu
                        v-if="artworkDownloadingStats"
                        :model-value="true"
                        activator="parent"
                        location="top center"
                        offset="0"
                        persistent
                        :close-on-content-click="false"
                        :open-on-click="false"
                        :open-on-hover="false"
                        class="default-tooltip"
                      >
                        <VCard
                          max-width="220"
                          width="220"
                          elevation="6"
                          rounded="lg"
                        >
                          <VCardText class="pa-4">
                            <!-- Header -->
                            <div class="d-flex align-center justify-space-between mb-2">
                              <span class="text-body-2 font-weight-medium">
                                Downloading files...
                              </span>
                            </div>
                            <!-- Progress -->
                            <VProgressLinear
                              :model-value="(artworkDownloadingStats.triggered / artworkDownloadingStats.total) * 100"
                              height="6"
                              rounded
                              stream
                              class="mb-2"
                              color="primary"
                            />
                            <!-- Stats -->
                            <div class="text-caption text-medium-emphasis">
                              {{ artworkDownloadingStats.triggered }} / {{ artworkDownloadingStats.total }} downloaded •
                              {{ artworkDownloadingStats.remaining }} remaining
                            </div>
                          </VCardText>
                        </VCard>
                      </VMenu>
                      <VBtn
                        :disabled="!!exportLoading"
                        class="ma-1 border"
                        color="primary"
                        v-bind="props"
                        variant="flat"
                        size="small"
                      >
                        <VProgressCircular
                          v-if="exportLoading"
                          size="x-small"
                          :width="3"
                          indeterminate
                          color="light"
                          class="mr-1"
                        />
                        Export
                      </VBtn>
                    </div>
                  </template>
                  <VCard class="export-menu-body">
                    <VList
                      v-model:selected="selectedExportCategories"
                      select-strategy="leaf"
                      class="py-1"
                    >
                      <VListItem
                        v-for="(exportOption, exportOptionKey) in exportCategoryOptions"
                        :key="exportOptionKey"
                        :title="`Export ${capitalize(exportOption)}`"
                        :value="exportOption"
                        class="py-1 mx-1"
                      >
                        <template v-slot:prepend="{ isSelected }">
                          <VListItemAction start>
                            <VCheckboxBtn :model-value="isSelected" />
                          </VListItemAction>
                        </template>
                      </VListItem>
                    </VList>
                    <div class="text-center px-1">
                      <VBtn
                        :disabled="!selectedExportCategories?.length"
                        class="w-100"
                        size="x-small"
                        @click="exportBrandingItems"
                      >
                        <VIcon icon="tabler-download" />
                        <span
                          v-if="selectedLineItemIds?.length"
                        >
                        ({{ selectedLineItemIds.length }})
                      </span>
                      </VBtn>
                    </div>
                  </VCard>
                </VMenu>
                <VBtn
                  v-if="isBrandingGlobalSearch"
                  class="ma-1 button-disabled-gray"
                  color="primary"
                  variant="outlined"
                  size="small"
                  @click="showProjectFilesDialog = true"
                >
                  <VIcon
                    start
                    icon="tabler-folders"
                  />
                  Show Files
                </VBtn>
                <VBtn
                  v-if="isBrandingGlobalSearch"
                  class="ma-1"
                  color="primary"
                  variant="flat"
                  size="small"
                  @click="openProjectFilesUploadDialog"
                >
                  <VIcon
                    start
                    icon="tabler-upload"
                  />
                  Upload Files
                </VBtn>
                <VBtn
                  v-if="isBrandingWorkOrderView"
                  class="ma-1"
                  color="primary"
                  variant="outlined"
                  size="small"
                  @click="showProjectFilesDialog = true"
                >
                  <VIcon
                    start
                    icon="tabler-folders"
                  />
                  Show Files
                </VBtn>
                <VBtn
                  v-if="isBrandingWorkOrderView"
                  class="ma-1"
                  color="primary"
                  variant="flat"
                  size="small"
                  @click="openProjectFilesUploadDialog"
                >
                  <VIcon
                    start
                    icon="tabler-upload"
                  />
                  Upload Files
                </VBtn>
                <VBtn
                  v-if="!isGlobalSearch && (isWfLineItems || isKopLineItems) && can('view', 'projects')"
                  :to="isWfLineItems ? `/projects/signs/${route.params.id}` : `/projects/kop/${route.params.id}`"
                  size="small"
                  class="pa-1 ma-1"
                >
                  Map View
                </VBtn>
                <VBtn
                  v-if="!isGlobalSearch && isProjectOnlyView && can('change', 'campaign')"
                  :to="`/projects/${route.params.id}/edit`"
                  size="small"
                  class="ma-1"
                >
                  Edit
                </VBtn>
              </VCol>
            </VRow>
          </VCol>
        </VRow>
        <VRow
          v-if="!isGlobalSearch && !isProjectOnlyView"
          class="mt-1"
        >
          <VCol
            cols="12"
            sm="4"
            md="3"
            lg=""
            class="py-1"
          >
            <label class="text-uppercase text-body-2">
              <strong>Customer Name</strong>
            </label>
            <h5>
              {{ workOrder?.customer_name || '-' }}
            </h5>
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md="3"
            lg=""
            class="py-1"
          >
            <label class="text-uppercase text-body-2">
              <strong>Order No</strong>
            </label>
            <h5>
              {{ workOrder?.order_no || '-' }}
            </h5>
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md="3"
            lg=""
            class="py-1"
          >
            <label class="text-uppercase text-body-2">
              <strong>Entry Date</strong>
            </label>
            <h5>
              {{ workOrder?.event_date || '-' }}
            </h5>
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md="3"
            lg=""
            class="py-1"
          >
            <label class="text-uppercase text-body-2">
              <strong>CS Name</strong>
            </label>
            <h5>
              {{ workOrder?.cs_coordinator || '-' }}
            </h5>
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md=""
            lg=""
            class="py-1"
          >
            <label class="text-uppercase text-body-2">
              <strong>Venue Name</strong>
            </label>
            <h5>
              {{ workOrder?.venue || '-' }}
            </h5>
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md=""
            lg=""
            class="py-1"
          >
            <label class="text-uppercase text-body-2">
              <strong>IM/PM Name</strong>
            </label>
            <h5>
              {{ workOrder?.project_manager || '-' }}
            </h5>
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md=""
            lg=""
            class="py-1 wo-category-title-block"
          >
            <label class="text-uppercase text-body-2">
              <strong>Category 1</strong>
              <VBtn
                v-if="isActiveProject"
                size=""
                class="wo-category-title-block__btn"
                color="secondary"
                @click="showUpdateWorkOrderDialog = true"
              >
                <VIcon
                  size="11"
                  icon="tabler-pencil"
                />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    Update Category/Budget
                  </span>
                </VMenu>
              </VBtn>
            </label>
            <h5>
              {{ workOrder?.category_1_display || '-' }}
            </h5>
          </VCol>
          <VCol
            cols="12"
            sm="4"
            md=""
            lg=""
            class="py-1 wo-category-title-block"
          >
            <label class="text-uppercase text-body-2">
              <strong>Category 2</strong>
              <VBtn
                v-if="isActiveProject"
                size=""
                class="wo-category-title-block__btn"
                color="secondary"
                @click="showUpdateWorkOrderDialog = true"
              >
                <VIcon
                  size="11"
                  icon="tabler-pencil"
                />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    Update Category/Budget
                  </span>
                </VMenu>
              </VBtn>
            </label>
            <h5>
              {{ workOrder?.category_2_display || '-' }}
            </h5>
          </VCol>
          <VCol
            v-if="can('view', 'finances')"
            cols="12"
            sm="4"
            md=""
            lg=""
            class="py-1 wo-category-title-block"
          >
            <label class="text-uppercase text-body-2">
              <strong>Budget</strong>
              <VBtn
                v-if="isActiveProject"
                size=""
                class="wo-category-title-block__btn"
                color="secondary"
                @click="showUpdateWorkOrderDialog = true"
              >
                <VIcon
                  size="11"
                  icon="tabler-pencil"
                />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    Update Category/Budget
                  </span>
                </VMenu>
              </VBtn>
            </label>
            <h5>
              {{ workOrder?.budget || '0' }}
            </h5>
          </VCol>
        </VRow>
      </VContainer>
    </VCard>
    <VCard
      class="mt-1"
    >
      <VOverlay
        v-model="lineItemsLoading"
        persistent
        absolute
        contained
        class="align-center justify-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VOverlay>
      <VContainer fluid>
        <VRow v-if="isGlobalSearch">
          <VCol
            cols="12"
            md="6"
            lg=""
          >
            <MultipleEventGroupSelect
              v-model="brandingStore.globalFilters.eventGroupFilter"
              :select-options="programOptions"
              :loading="globalOptionsLoading"
              :active-branding-campaigns-only="!isBrandingGlobalSearch || isActiveProjectsFilter"
              @update:focused="programOptions.length ? '' : getGlobalLineItemOptions()"
              @update:model-value="getLineItems(); onUpdateFilters(); setQuery('programs', brandingStore.globalFilters.eventGroupFilter?.length ? JSON.stringify(brandingStore.globalFilters.eventGroupFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            md="6"
            lg=""
          >
            <MultipleCampaignsSelect
              v-model="brandingStore.globalFilters.projectFilter"
              :select-options="projectOptions"
              :type="2"
              :loading="globalOptionsLoading"
              :is-select-all-visible="true"
              work-orders-included
              @update:focused="projectOptions.length ? '' : getGlobalLineItemOptions()"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('projects', brandingStore.globalFilters.projectFilter?.length ? JSON.stringify(brandingStore.globalFilters.projectFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
            lg=""
          >
            <MultipleSuppliersSelect
              v-model="brandingStore.globalFilters.supplierFilter"
              label="Supplier"
              :select-options="supplierOptions"
              :loading="globalOptionsLoading"
              @update:focused="supplierOptions.length ? '' : getGlobalLineItemOptions()"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('suppliers', brandingStore.globalFilters.supplierFilter?.length ? JSON.stringify(brandingStore.globalFilters.supplierFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
            lg=""
          >
            <MultipleCampaignCategory1Select
              v-model="brandingStore.globalFilters.category1Filter"
              :select-options="category1Options"
              :loading="globalOptionsLoading"
              @update:focused="category1Options.length ? '' : getGlobalLineItemOptions(); setQuery('categories_1', brandingStore.globalFilters.category1Filter?.length ? JSON.stringify(brandingStore.globalFilters.category1Filter) : undefined)"
              @update:modelValue="getLineItems(); onUpdateFilters()"
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
            lg=""
          >
            <MultipleCampaignCategory2Select
              v-model="brandingStore.globalFilters.category2Filter"
              :select-options="category2Options"
              :loading="globalOptionsLoading"
              @update:focused="category2Options.length ? '' : getGlobalLineItemOptions(); setQuery('categories_2', brandingStore.globalFilters.category2Filter?.length ? JSON.stringify(brandingStore.globalFilters.category2Filter) : undefined)"
              @update:modelValue="getLineItems(); onUpdateFilters()"
            />
          </VCol>
        </VRow>
        <VRow>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg=""
          >
            <MultipleUsersSelect
              v-model="computedResponsibleFilter"
              :select-options="responsibleOptions"
              :loading="globalOptionsLoading"
              :label="'Responsible'"
              @update:focused="onResponsibleOptionsFocus"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('responsible', computedResponsibleFilter?.length ? JSON.stringify(computedResponsibleFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg=""
          >
            <MultipleUsersSelect
              v-model="computedAssignedToFilter"
              :select-options="assignedToOptions"
              :loading="globalOptionsLoading"
              :label="'Assigned To'"
              @update:focused="onAssignedToOptionsFocus"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('assigned_to', computedAssignedToFilter?.length ? JSON.stringify(computedAssignedToFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg=""
          >
            <MultipleCampaignItemStatusSelect
              v-model="computedStatusFilter"
              :select-options="statusOptions"
              :loading="globalOptionsLoading"
              @update:focused="onStatusOptionsFocus"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('item_statuses', computedStatusFilter?.length ? JSON.stringify(computedStatusFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg=""
          >
            <MultipleBrandingItemZoneSelect
              v-model="computedZoneFilter"
              :select-options="zoneOptions"
              :loading="globalOptionsLoading"
              @update:focused="onZoneOptionsFocus"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('zones', computedZoneFilter?.length ? JSON.stringify(computedZoneFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg=""
          >
            <MultipleBrandingItemAreaSelect
              v-model="computedAreaFilter"
              :select-options="areaOptions"
              :loading="globalOptionsLoading"
              @update:focused="onAreaOptionsFocus"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('areas', computedAreaFilter?.length ? JSON.stringify(computedAreaFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg=""
          >
            <VAutocomplete
              v-model="supplierErpFilter"
              :items="supplierErpOptions"
              label="Supplier ERP"
              multiple
              chips
              clearable
              closable-chips
              density="compact"
              :loading="globalOptionsLoading"
              @update:focused="supplierErpOptions.length ? '' : getGlobalLineItemOptions()"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('supplier_erp', supplierErpFilter?.length ? JSON.stringify(supplierErpFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            lg="3"
          >
            <VRow>
              <VCol
                cols="12"
                sm="6"
              >
                <AppDateTimePicker
                  v-model="dueDateStart"
                  prepend-inner-icon="tabler-calendar"
                  :config="{ dateFormat: 'Y-m-d' }"
                  placeholder="Due Date Start"
                  clearable
                  @input="getLineItems(); onUpdateFilters(); setQuery('due_date_start', dueDateStart?.toString() || undefined)"
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <AppDateTimePicker
                  v-model="dueDateEnd"
                  prepend-inner-icon="tabler-calendar"
                  :config="{ dateFormat: 'Y-m-d' }"
                  placeholder="Due Date End"
                  clearable
                  @input="getLineItems(); onUpdateFilters(); setQuery('due_date_end', dueDateEnd?.toString() || undefined)"
                />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
        <VRow>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg="6"
            order="3"
            order-md="1"
            class="d-flex align-center justify-space-between flex-wrap"
          >
            <div
              class="d-flex align-center flex-wrap"
            >
              <div
                class="d-flex align-center flex-wrap"
              >
                <VMenu
                  v-if="
                    !isLinkCreatedUser
                    &&
                    isActiveProject
                    && (
                      can('change', 'lineitemresponsible')
                      || can('change', 'lineitemassignedto')
                      || can('change', 'lineitemstatus')
                      || can('change', 'lineitemonhold')
                      || can('change', 'lineitempriority')
                      || can('change', 'lineitem')
                      || can('hide', 'lineitem')
                      || can('add', 'user')
                      || userStore.isSupplier
                    )
                  "
                >
                  <template #activator="{ props }">
                    <VBtn
                      class="ma-1 border"
                      size="small"
                      variant="flat"
                      color="secondary"
                      v-bind="props"
                      :disabled="!selectedLineItemIds.length || isActionLoading"
                    >
                      Update
                      <VIcon
                        icon="tabler-chevron-down"
                      />
                      <VProgressCircular
                        v-if="updateBtnLoading"
                        class="ml-1"
                        size="16"
                        width="2"
                        indeterminate
                        color="white"
                      />
                    </VBtn>
                  </template>

                  <VList>
<!--                    // TODO: Uncomment after Share by Email integration-->
<!--                    <VListItem-->
<!--                      v-if="can('add', 'user')"-->
<!--                      :disabled="!isAllSelectedItemsIsCanUserSelect"-->
<!--                      @click="showShareItemsByEmailDialog = true"-->
<!--                    >-->
<!--                      Share by Email-->
<!--                      <div-->
<!--                        v-if="!isAllSelectedItemsIsCanUserSelect"-->
<!--                        class="update-action-warning-message"-->
<!--                      >-->
<!--                        Cannot share item(s): <br>-->
<!--                        you don't have permission for all selected items-->
<!--                      </div>-->
<!--                    </VListItem>-->
                    <!-- Hidden Item View -->
                    <template v-if="hiddenVisibilityFilter">
                      <VListItem
                        v-if="can('hide', 'lineitem')"
                        :disabled="!isAllSelectedItemsIsCanUserSelect"
                        @click="multipleUpdateItemVisibility(1)"
                      >
                        Unhide Item(s)
                        <div
                          v-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot "Unhide Item(s)": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                    </template>
                    <!-- /Hidden Item View -->
                    <template v-else>
<!--                    // TODO: Uncomment after Share by Email integration / Shared Upload Link integration-->
<!--                      <VListItem-->
<!--                        v-if="isBrandingGlobalSearch"-->
<!--                        @click="openSharedUploadLinkDialog"-->
<!--                      >-->
<!--                        <template #prepend>-->
<!--                          <VIcon-->
<!--                            icon="tabler-link"-->
<!--                            size="18"-->
<!--                            class="mr-2"-->
<!--                          />-->
<!--                        </template>-->
<!--                        Shared Upload Link-->
<!--                      </VListItem>-->
                      <VListItem
                        v-if="can('change', 'lineitemresponsible')"
                        :disabled="!isAllSelectedItemsFromOneProject || !isAllSelectedItemsIsCanUserSelect"
                        @click="selectedAssigneeType = 'responsible'; showItemAssigneeDialog = true"
                      >
                        Responsible
                        <div
                          v-if="!isAllSelectedItemsFromOneProject"
                          class="update-action-warning-message"
                        >
                          Select items from the same project <br>
                          to update "Responsible"
                        </div>
                        <div
                          v-else-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot update "Responsible": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                      <VListItem
                        v-if="can('change', 'lineitemassignedto')"
                        :disabled="!isAllSelectedItemsFromOneProject || !isAllSelectedItemsIsCanUserSelect"
                        @click="selectedAssigneeType = 'assigned_to'; showItemAssigneeDialog = true"
                      >
                        Assigned To
                        <div
                          v-if="!isAllSelectedItemsFromOneProject"
                          class="update-action-warning-message"
                        >
                          Select items from the same project <br>
                          to update "Assigned To"
                        </div>
                        <div
                          v-else-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot update "Assigned To": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                      <VListItem
                        v-if="can('change', 'lineitemstatus')"
                        :disabled="!isAllSelectedItemsFromOneProject || !isAllSelectedItemsIsNotOnHold || !isAllSelectedItemsIsCanUserSelect"
                        @click="showItemStatusDialog = true"
                      >
                        Status(es)
                        <div
                          v-if="!isAllSelectedItemsFromOneProject"
                          class="update-action-warning-message"
                        >
                          Select items from the same project <br>
                          to update "Status(es)"
                        </div>
                        <div
                          v-else-if="!isAllSelectedItemsIsNotOnHold"
                          class="update-action-warning-message"
                        >
                          Select items without "On Hold" <br>
                          to update "Status(es)"
                        </div>
                        <div
                          v-else-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot update "Status(es)": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                      <VListItem
                        v-if="can('change', 'lineitemonhold')"
                        :disabled="!isAllSelectedItemsIsCanUserSelect"
                        @click="multipleUpdateLineItemOnHold(true)"
                      >
                        Set "On Hold"
                        <div
                          v-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot Set "On Hold": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                      <VListItem
                        v-if="can('change', 'lineitemonhold')"
                        :disabled="!isAllSelectedItemsIsCanUserSelect"
                        @click="multipleUpdateLineItemOnHold(false)"
                      >
                        Remove "On Hold"
                        <div
                          v-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot Remove "On Hold": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                      <VListItem
                        v-if="can('change', 'lineitempriority')"
                        :disabled="!isAllSelectedItemsIsCanUserSelect"
                        @click="showItemPriorityDialog = true"
                      >
                        Priority
                        <div
                          v-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot update "Priority": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                      <VListItem
                        v-if="can('change', 'lineitem') || !!userStore.isSupplier"
                        @click="showItemExtRefNumDialog = true"
                      >
                        Ext Ref #
                      </VListItem>
                      <VListItem
                        v-if="can('hide', 'lineitem')"
                        :disabled="!isAllSelectedItemsIsCanUserSelect"
                        @click="multipleUpdateItemVisibility(2)"
                      >
                        Hide Item(s) (Keep in Finances)
                        <div
                          v-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot "Hide Item(s)": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                      <VListItem
                        v-if="can('hide', 'lineitem')"
                        :disabled="!isAllSelectedItemsIsCanUserSelect"
                        @click="multipleUpdateItemVisibility(3)"
                      >
                        Hide Item(s) (Remove from Finances)
                        <div
                          v-if="!isAllSelectedItemsIsCanUserSelect"
                          class="update-action-warning-message"
                        >
                          Cannot "Hide Item(s)": <br>
                          you don't have permission for all selected items
                        </div>
                      </VListItem>
                    </template>
                  </VList>
                </VMenu>
                <template v-if="
                  isAllSelectedItemsIsCanUserApprove &&
                  isActiveProject &&
                  (isLinkCreatedUser || !userStore.isSupplier || userStore.isSupplierProjectManagement) &&
                  isAllSelectedItemsIsNotOnHold
                ">
                  <VBtn
                    size="small"
                    color="success"
                    class="text-none ma-1"
                    :disabled="!selectedLineItemIds.length || isActionLoading"
                    @click="handleApproveItemsClick"
                  >
                    <VIcon
                      icon="tabler-check"
                    />
                    Approve Item(s)
                    <VProgressCircular
                      v-if="approveLoading"
                      class="ml-1"
                      size="16"
                      width="2"
                      indeterminate
                      color="white"
                    />
                  </VBtn>
                  <VBtn
                    size="small"
                    class="text-none ma-1"
                    :disabled="!selectedLineItemIds.length || isActionLoading"
                    @click="handleRejectItemsClick"
                  >
                    <VIcon
                      icon="tabler-x"
                    />
                    Reject Item(s)
                    <VProgressCircular
                      v-if="rejectLoading"
                      class="ml-1"
                      size="16"
                      width="2"
                      indeterminate
                      color="white"
                    />
                  </VBtn>
                </template>
                <VAlert
                  v-if="
                    selectedLineItemIds.length &&
                    !isAllSelectedItemsIsNotOnHold &&
                    userStore.isProjectManagementRole
                  "
                  show
                  variant="tonal"
                  color="warning"
                  class="text-body-2 py-1"
                >
                  Select items without "On Hold" to Approve/Reject
                </VAlert>
              </div>
            </div>
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg="3"
            order="1"
            order-md="2"
          >
            <MultipleBasePrioritySelect
              v-model="computedPriorityFilter"
              :select-options="[...priorityOptions]"
              :is-select-all-visible="true"
              :loading="globalOptionsLoading"
              @update:focused="onPriorityOptionsFocus"
              @update:modelValue="getLineItems(); onUpdateFilters(); setQuery('priority__in', computedPriorityFilter?.length ? JSON.stringify(computedPriorityFilter) : undefined)"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
            lg="3"
            order="2"
            order-md="3"
          >
            <VTextField
              v-model="lineItemSearch"
              density="compact"
              label="Search"
              append-inner-icon="mdi-magnify"
              :type="searchBy === 'line_no' ? 'number' : 'text'"
              @input="onLineItemSearch"
            >
              <template #prepend>
                <VSelect
                  v-model="searchBy"
                  :items="searchByItems"
                  :disabled="!!lineItemsLoading"
                  item-value="value"
                  item-title="title"
                  label="Search by"
                  @update:modelValue="lineItemSearch = ''; updateSearchQuery(); getLineItems(); onUpdateFilters()"
                />
              </template>
            </VTextField>
          </VCol>
        </VRow>
        <VRow>
          <VCol>
            <ViewAllLineItemListTable
              v-model:selected-line-item-ids="selectedLineItemIds"
              v-model:line-item-table-options="computedLineItemTableOptions"
              v-model:line-items="lineItems"
              v-model:selected-history-line-item-id="selectedHistoryLineItemId"
              v-model:show-line-item-status-history-dialog="showLineItemStatusHistoryDialog"
              v-model:selected-chat-line-item-id="selectedChatLineItemId"
              v-model:show-global-chat-dialog="showGlobalChatDialog"
              v-model:selected-line-item-for-review-dialog="selectedLineItemForReviewDialog"
              v-model:show-line-item-review-dialog="showLineItemReviewDialog"
              v-model:show-table-column-settings-dialog="showTableColumnSettingsDialog"
              v-model:is-on-hold-filter="isOnHoldFilter"
              v-model:is-released-filter="isReleasedFilter"
              v-model:hidden-visibility-filter="hiddenVisibilityFilter"
              v-model:rejected-items-only-filter="rejectedItemsOnlyFilter"
              :line-items-count="lineItemsCount"
              :line-items-loading="lineItemsLoading"
              :line-item-headers="lineItemHeaders"
              :hidden-visibility-filter="hiddenVisibilityFilter"
              :is-show-item-review-dialog-instead-of-review-page="
                !!isGlobalSearch || !!isWfLineItems || !!isKopLineItems|| !!isRetailLineItems
              "
              :get-line-item-review-link="getLineItemReviewLink"
              :campaign-type="campaignType"
              :show-active-projects-filter="isBrandingGlobalSearch"
              :is-active-projects-filter="isActiveProjectsFilter"
              :is-on-hold-options="isOnHoldOptions"
              @handleActiveProjectsFilterChange="handleActiveProjectsFilterChange"
              @handleOnHoldFilterChange="handleOnHoldFilterChange"
              @handleReleasedFilterChange="handleReleasedFilterChange"
              @handleHiddenVisibilityFilterChange="handleHiddenVisibilityFilterChange"
              @handleRejectedItemsOnlyFilterChange="handleRejectedItemsOnlyFilterChange"
              @onUpdateTableOptions="onUpdateTableOptions"
            />
          </VCol>
        </VRow>
      </VContainer>
    </VCard>
    <!-- Add new dialog for reject status selection -->
    <VDialog
      v-model="showRejectStatusDialog"
      max-width="400"
    >
      <VCard>
        <VCardTitle>
          Select Reject Status
        </VCardTitle>
        <VCardText>
          <VSelect
            v-model="selectedRejectStatus"
            :items="rejectStatusOptions"
            item-title="name"
            item-value="id"
            label="Reject Status"
            :loading="rejectLoading"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            variant="text"
            @click="showRejectStatusDialog = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            :loading="rejectLoading"
            :disabled="!selectedRejectStatus"
            @click="handleRejectStatusSelect"
          >
            Reject
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss">
.branding-line-item-list-container {
}

.update-action-warning-message {
  font-size: 10px;
  opacity: 0.6;
}

.export-menu-body {
  .v-list .v-list-item:first-child {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .v-list .v-list-item:last-child {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .v-list .v-list-item {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
}

.files-upload-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 132px;
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.02);
  color: rgba(var(--v-theme-on-surface), 0.72);
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.files-upload-dropzone--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}

.files-upload-result-panel {
  height: 100%;
  padding: 14px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.files-upload-result-list {
  background: transparent;
}

.shared-upload-items-table {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.shared-upload-link-field {
  min-width: 0;
  flex: 1 1 420px;
}

.wo-category-title-block {
  .wo-category-title-block__btn {
    padding: 1px;
    margin-left: 2px;
    margin-top: -2px;
    border-radius: 15%;
  }
  .wo-category-title-block__subtitle {
    line-height: 9px;
    font-size: 9px;
  }
}
</style>
